import { useCallback } from "react";
import { useWebHaptics } from "web-haptics/react";
import { WebHaptics } from "web-haptics";
import { toast } from "sonner";

// Predefined vibration patterns
export const HAPTIC_PATTERNS = {
  success: [
    { duration: 30 },
    { delay: 60, duration: 40, intensity: 1 },
  ],
  nudge: [
    { duration: 80, intensity: 0.8 },
    { delay: 80, duration: 50, intensity: 0.3 },
  ],
  error: [
    { duration: 40, intensity: 0.7 },
    { delay: 40, duration: 40, intensity: 0.7 },
    { delay: 40, duration: 40, intensity: 0.9 },
    { delay: 40, duration: 50, intensity: 0.6 },
  ],
  buzz: {
    pattern: [{ duration: 1000 }],
    options: { intensity: 1 },
  },
  soft: [{ duration: 40 }],
  selection: {
    pattern: [{ duration: 8 }],
    options: { intensity: 0.3 },
  },
};

let globalHaptics = null;

export const getGlobalHaptics = () => {
  if (!globalHaptics && typeof window !== "undefined") {
    try {
      globalHaptics = new WebHaptics();
    } catch (error) {
      console.error("Failed to initialize WebHaptics:", error);
    }
  }
  return globalHaptics;
};

/**
 * Standalone vibration trigger (usable inside or outside React components)
 * @param {"success" | "nudge" | "error" | "buzz" | "soft" | "selection" | Array} typeOrPattern
 */
export const triggerHaptic = (typeOrPattern = "selection") => {
  try {
    const haptics = getGlobalHaptics();
    if (!haptics) return;

    if (typeof typeOrPattern === "string") {
      const config = HAPTIC_PATTERNS[typeOrPattern.toLowerCase()];
      if (!config) return;

      if (Array.isArray(config)) {
        haptics.trigger(config);
      } else if (config.pattern) {
        haptics.trigger(config.pattern, config.options);
      }
    } else if (Array.isArray(typeOrPattern)) {
      haptics.trigger(typeOrPattern);
    }
  } catch (error) {
    console.error("Haptics vibrate error:", error);
  }
};

// Automatically intercept toast.error from sonner to trigger error haptic
if (typeof toast !== "undefined" && typeof toast.error === "function" && !toast._hapticsPatched) {
  const originalToastError = toast.error.bind(toast);
  toast.error = (...args) => {
    try {
      triggerHaptic("error");
    } catch (e) {
      console.error("Haptics trigger error in toast.error:", e);
    }
    return originalToastError(...args);
  };
  toast._hapticsPatched = true;
}

export default function useHaptics() {
  const { trigger: rawTrigger, isSupported } = useWebHaptics();

  /**
   * Vibrate device by preset pattern name or custom pattern
   * @param {"success" | "nudge" | "error" | "buzz" | "soft" | "selection" | Array} typeOrPattern
   */
  const vibrate = useCallback(
    (typeOrPattern = "selection") => {
      try {
        if (typeof typeOrPattern === "string") {
          const config = HAPTIC_PATTERNS[typeOrPattern.toLowerCase()];
          if (!config) return;

          if (Array.isArray(config)) {
            rawTrigger(config);
          } else if (config.pattern) {
            rawTrigger(config.pattern, config.options);
          }
        } else if (Array.isArray(typeOrPattern)) {
          rawTrigger(typeOrPattern);
        }
      } catch (error) {
        console.error("Haptics vibrate error:", error);
      }
    },
    [rawTrigger]
  );

  return {
    vibrate,
    // Alias trigger for backwards compatibility
    trigger: vibrate,
    isSupported,
    // Convenience helper methods
    vibrateSuccess: useCallback(() => vibrate("success"), [vibrate]),
    vibrateNudge: useCallback(() => vibrate("nudge"), [vibrate]),
    vibrateError: useCallback(() => vibrate("error"), [vibrate]),
    vibrateBuzz: useCallback(() => vibrate("buzz"), [vibrate]),
    vibrateSoft: useCallback(() => vibrate("soft"), [vibrate]),
    vibrateSelection: useCallback(() => vibrate("selection"), [vibrate]),
  };
}
